// Telefonnummern aus dem Formular richtig zusammensetzen.
//
// Anlass (Callcenter, 28.08.2026): „Ich merke oft, dass Kunden die Nummer nicht
// richtig beim Formular eingeben, z. B. die Vorwahl doppelt — dadurch kann man
// die nicht anrufen, außer man bemerkt das und justiert die Nummer."
//
// Hier war die Ursache. Vorher wurde die Nummer so gebaut:
//
//     `${prefix}${phone.replace(/\D/g, '').replace(/^0+/, '')}`
//
// `\D` wirft *jedes* Zeichen weg, das keine Ziffer ist — also auch das Plus,
// das die Person selbst getippt hat. Wer „+49 176 1234567" eintrug, bekam
// „+49" + „491761234567" = „+49491761234567". Die Landesvorwahl stand zweimal
// drin, und weil das eine formal gültige E.164-Nummer ist (sie liest sich als
// Festnetznummer aus Leer, 0491), fiel es nirgends auf: im CRM wurde sie
// wirklich gewählt, der Anruf scheiterte, der Versuch war verbraucht.
//
// Nachgezählt am Bestand (28.08.2026): 125 von 1.229 Anfragen aus diesem
// Formular — jede zehnte — sind so gespeichert.
//
// Regel jetzt: **Die Länderauswahl ist ein Hinweis, kein Text, der vorne
// angeklebt wird.** Steht im Feld schon eine eigene Landesvorwahl, gewinnt die.

/** Länder, die in Berlin vorkommen — Deutschland zuerst und voreingestellt. */
export const LAENDER: { code: string; name: string; vorwahl: string }[] = [
  { code: 'DE', name: 'Deutschland', vorwahl: '49' },
  { code: 'AT', name: 'Österreich', vorwahl: '43' },
  { code: 'CH', name: 'Schweiz', vorwahl: '41' },
  { code: 'TR', name: 'Türkei', vorwahl: '90' },
  { code: 'PL', name: 'Polen', vorwahl: '48' },
  { code: 'RU', name: 'Russland', vorwahl: '7' },
  { code: 'UA', name: 'Ukraine', vorwahl: '380' },
  { code: 'RO', name: 'Rumänien', vorwahl: '40' },
  { code: 'BG', name: 'Bulgarien', vorwahl: '359' },
  { code: 'IT', name: 'Italien', vorwahl: '39' },
  { code: 'FR', name: 'Frankreich', vorwahl: '33' },
  { code: 'ES', name: 'Spanien', vorwahl: '34' },
  { code: 'PT', name: 'Portugal', vorwahl: '351' },
  { code: 'NL', name: 'Niederlande', vorwahl: '31' },
  { code: 'BE', name: 'Belgien', vorwahl: '32' },
  { code: 'DK', name: 'Dänemark', vorwahl: '45' },
  { code: 'SE', name: 'Schweden', vorwahl: '46' },
  { code: 'CZ', name: 'Tschechien', vorwahl: '420' },
  { code: 'HR', name: 'Kroatien', vorwahl: '385' },
  { code: 'RS', name: 'Serbien', vorwahl: '381' },
  { code: 'GR', name: 'Griechenland', vorwahl: '30' },
  { code: 'GB', name: 'Vereinigtes Königreich', vorwahl: '44' },
  { code: 'IE', name: 'Irland', vorwahl: '353' },
  { code: 'US', name: 'USA / Kanada', vorwahl: '1' },
  { code: 'BR', name: 'Brasilien', vorwahl: '55' },
  { code: 'SY', name: 'Syrien', vorwahl: '963' },
  { code: 'LB', name: 'Libanon', vorwahl: '961' },
  { code: 'IL', name: 'Israel', vorwahl: '972' },
  { code: 'IR', name: 'Iran', vorwahl: '98' },
  { code: 'IQ', name: 'Irak', vorwahl: '964' },
  { code: 'AF', name: 'Afghanistan', vorwahl: '93' },
  { code: 'MA', name: 'Marokko', vorwahl: '212' },
  { code: 'EG', name: 'Ägypten', vorwahl: '20' },
  { code: 'VN', name: 'Vietnam', vorwahl: '84' },
  { code: 'IN', name: 'Indien', vorwahl: '91' },
  { code: 'CN', name: 'China', vorwahl: '86' },
  { code: 'JP', name: 'Japan', vorwahl: '81' },
  { code: 'KR', name: 'Südkorea', vorwahl: '82' },
  { code: 'AU', name: 'Australien', vorwahl: '61' },
  { code: 'ZA', name: 'Südafrika', vorwahl: '27' },
];

/** „DE" → 🇩🇪. Windows zeigt die Buchstaben statt der Flagge — deshalb stehen
 *  Name und Vorwahl in der Auswahl ohnehin daneben. */
export function flagge(code: string): string {
  return code.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Sieht die Ziffernfolge (ohne Landesvorwahl) wie eine deutsche Mobilnummer
// aus? Kalibriert am Bestand: 015x/016x/017x mit 10 oder 11 Ziffern trifft auf
// 36.823 von 36.930 Mobilnummern zu.
function istMobilDE(nsn: string): boolean {
  return /^1[5-7]\d{7,8}$/.test(nsn);
}

// Nimmt eine doppelt eingetragene Landesvorwahl heraus („4949176…" →
// „49176…"). Nur, wenn dahinter eine vollständige Mobilnummer steckt und die
// Ziffernfolge als Ganzes keine ist — sonst gingen echte Nummern aus dem
// Ortsnetz 0491 (Leer) kaputt: „16543" ist keine Mobilnummer.
// Gleiche Regel wie im CRM, damit beide Seiten dieselbe Nummer bekommen.
//
// Sie greift damit nur für deutsche Mobilnummern: bei ausländischen Nummern
// fehlt die verlässliche Längenkennung, um eine Dopplung von einer echten
// Nummer zu unterscheiden. Im Bestand sind das Einzelfälle (7 von 1.229), und
// eine falsche „Reparatur" wäre dort schlimmer als keine.
function ohneDoppelteVorwahl(ziffern: string, cc: string): string {
  if (!ziffern.startsWith(cc)) return ziffern;
  const nsn = ziffern.slice(cc.length);
  if (!nsn.startsWith(cc)) return ziffern;
  const ohne = nsn.slice(cc.length);
  if (istMobilDE(ohne) && !istMobilDE(nsn)) return cc + ohne;
  return ziffern;
}

/**
 * Baut aus der Eingabe und der gewählten Länder-Vorwahl eine Nummer in
 * internationaler Schreibweise (+49…).
 *
 * Es ist gleich, wie jemand die Nummer hinschreibt: mit 0 oder ohne, mit
 * „+49", mit „0049", mit Schrägstrich, Klammern oder Leerzeichen.
 */
export function zuE164(eingabe: string, gewaehlteVorwahl: string): string {
  const cc = gewaehlteVorwahl.replace(/\D/g, '') || '49';
  const roh = (eingabe ?? '').replace(/[\s.\-()/]/g, '');
  if (!roh) return '';

  // Eigene Landesvorwahl im Feld — die gewinnt über die Auswahl. Genau das
  // fehlte vorher.
  if (roh.startsWith('+')) {
    let ziffern = roh.slice(1).replace(/\D/g, '');
    if (!ziffern) return '';
    // „+49 0176…" — die nationale 0 gehört nicht hinter die Landesvorwahl.
    if (ziffern.startsWith(cc + '0')) {
      ziffern = cc + ziffern.slice(cc.length).replace(/^0+/, '');
    }
    return '+' + ohneDoppelteVorwahl(ziffern, cc);
  }

  const ziffern = roh.replace(/\D/g, '');
  if (!ziffern) return '';
  if (ziffern.startsWith('00')) return '+' + ohneDoppelteVorwahl(ziffern.slice(2), cc);
  // Führende 0 ist die nationale Verkehrsausscheidungsziffer und fällt weg.
  if (ziffern.startsWith('0')) {
    return '+' + ohneDoppelteVorwahl(cc + ziffern.replace(/^0+/, ''), cc);
  }
  return '+' + ohneDoppelteVorwahl(cc + ziffern, cc);
}

/**
 * Kann die Telefonanlage die Nummer überhaupt wählen? Nur daran wird das
 * Absenden gehindert — eine Anfrage zu verlieren ist schlimmer als eine Nummer
 * mit Zahlendreher aufzunehmen, zumal die E-Mail-Adresse ohnehin dabei ist und
 * das CRM auffällige Nummern eigens meldet.
 */
export function istWaehlbar(e164: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(e164);
}

/**
 * Sieht die Nummer für ihr Land plausibel aus? Das ist der freundliche Hinweis
 * unter dem Feld („sieht noch nicht vollständig aus"), *keine* Sperre.
 *
 * Bewusst großzügig: deutsche Mobilnummern haben 10 oder 11 Ziffern hinter der
 * Landesvorwahl (36.823 von 36.930 im Bestand), beim Festnetz wird eine weite
 * Spanne zugelassen, und ausländische Nummern werden gar nicht beurteilt.
 */
export function istPlausibel(e164: string): boolean {
  if (!istWaehlbar(e164)) return false;
  if (!e164.startsWith('+49')) return true;
  const nsn = e164.slice(3);
  if (/^1[5-7]/.test(nsn)) return nsn.length >= 10 && nsn.length <= 11;
  return nsn.length >= 6 && nsn.length <= 13;
}
